package com.iregained.site.components.sections.patients.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.patients.styles.FaqHeaderStyle
import com.iregained.site.components.sections.patients.styles.FaqItemWrapperStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Staggered Drop Down
@Composable
private fun Modifier.staggeredDropDown(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else (-20).px).transition(
        Transition.of("opacity", 0.5.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.5.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun PatientsFaqSection(data: PatientsFaqData = defaultPatientsFaqData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "patients-faq"
    var expandedIndex by remember { mutableStateOf(-1) }
    var isVisible by remember { mutableStateOf(false) }

    if (data.faqs.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px)
                        .padding(leftRight = if (isDesktop) 100.px else 0.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.headline.isNotBlank()) {
                        SectionHeader(
                            text = data.headline,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 40.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s)),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    Column(modifier = Modifier.fillMaxWidth()) {
                        Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(palette.custom.divider))
                        data.faqs.forEachIndexed { index, faq ->
                            key(faq.question) {
                                if (faq.question.isNotBlank() && faq.answer.isNotBlank()) {
                                    Box(
                                        modifier = Modifier.fillMaxWidth()
                                            .staggeredDropDown(isVisible, 150 + (index * 100))
                                    ) {
                                        FaqAccordionItem(
                                            faq = faq,
                                            palette = palette,
                                            isExpanded = expandedIndex == index,
                                            onClick = { expandedIndex = if (expandedIndex == index) -1 else index })
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FaqAccordionItem(
    faq: FaqItem, palette: com.iregained.site.theme.SitePalette, isExpanded: Boolean, onClick: () -> Unit
) {
    Column(modifier = FaqItemWrapperStyle.toModifier()) {
        Row(
            modifier = FaqHeaderStyle.toModifier().onClick { onClick() },
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Div(
                attrs = Modifier.weight(1f).fontSize(20.px)
                    .fontWeight(if (isExpanded) FontWeight.Bold else FontWeight.SemiBold)
                    .color(if (isExpanded) palette.brand.primary else palette.custom.darkText)
                    .transition(Transition.of("color", 300.ms)).padding(right = 20.px).toAttrs {
                        ref { htmlElement ->
                            htmlElement.innerHTML = faq.question
                            onDispose {}
                        }
                    })

            Box(
                modifier = Modifier.size(24.px).position(Position.Relative), contentAlignment = Alignment.Center
            ) {
                Box(
                    modifier = Modifier.width(16.px).height(2.px).borderRadius(2.px)
                        .backgroundColor(if (isExpanded) palette.brand.primary else palette.custom.darkText)
                        .transition(Transition.of("background-color", 300.ms))
                )
                Box(
                    modifier = Modifier.width(2.px).height(16.px).borderRadius(2.px)
                        .backgroundColor(if (isExpanded) palette.brand.primary else palette.custom.darkText).transition(
                            Transition.of("transform", 300.ms),
                            Transition.of("opacity", 300.ms),
                            Transition.of("background-color", 300.ms)
                        ).rotate(if (isExpanded) 90.deg else 0.deg).opacity(if (isExpanded) 0.percent else 100.percent)
                )
            }
        }
        if (isExpanded) {
            Box(modifier = Modifier.fillMaxWidth().padding(left = 16.px, right = 40.px, bottom = 24.px)) {
                Div(
                    attrs = Modifier.fontSize(16.px).color(palette.custom.darkText).fontWeight(FontWeight.Medium)
                        .lineHeight(1.6)
                        .toAttrs { ref { htmlElement -> htmlElement.innerHTML = faq.answer; onDispose {} } })
            }
        }
    }
}

data class FaqItem(val question: String, val answer: String)
data class PatientsFaqData(
    val sectionId: String? = null, val headline: String, val faqs: List<FaqItem>
)

val defaultPatientsFaqData = PatientsFaqData(
    sectionId = "patients-faq", headline = "Frequently Asked Questions", faqs = listOf(
        FaqItem(
            "Does it hurt?",
            "No, the MyHand<sup>®</sup> System is designed to be completely non-invasive and painless. You are always in control of the movements, and the system adapts to your comfort level."
        ), FaqItem(
            "Will it work for my specific type of stroke?",
            "The system is built on neuroplasticity principles that apply broadly to both ischemic and hemorrhagic strokes. However, results vary by individual. We recommend discussing this with your clinician."
        ), FaqItem(
            "Is the MyHand<sup>®</sup> System covered by personal healthcare insurance plans?",
            "Coverage eligibility varies significantly based on your provider and individual coverage structures. While direct automated billing is not standard, our team provides comprehensive, itemized digital receipts alongside clinical prescription tracking to facilitate reimbursement submittals with major healthcare insurers."
        ), FaqItem(
            "Can I apply for patient financing directly online?",
            "Yes. Our approved clinical network offers integrated access to trusted healthcare consumer credit lenders. The pre-qualification interface is fully digital, transparently reviewing custom multi-tier interest models and repayment lengths ranging from 12 to 36 months to align with your personal budget."
        ), FaqItem(
            "What happens if I need to extend my active home therapy rental period?",
            "If your clinical therapist recommends additional time to achieve further rehabilitation milestones, home rentals can be extended automatically on a simple month-to-month continuous billing format. You retain total control to initiate our direct-to-door prepaid package return protocols when you are ready."
        ), FaqItem(
            "Who handles technical setup assistance when acquiring a home unit?",
            "Whether purchased outright, financed, or rented, all consumer units dispatched directly to personal residences include complete unboxing guidelines alongside dedicated access to virtual setup sessions guided by our remote support specialists."
        )
    )
)